// ─────────────────────────────────────────────────────────────
// Google Analytics 4 : chargement, routage et envoi
// ─────────────────────────────────────────────────────────────
// Seul point du site qui parle à GA4 : quelle propriété reçoit quelle route,
// le chargement de gtag.js, les vues de page et les événements.
//
// gtag.js est chargé en direct, hors GTM, dans sa PROPRE file
// (`gaDataLayer`, paramètre `l=` du script). GTM reste sur la page pour les
// seules balises Google Ads et garde `window.dataLayer`. Deux files séparées,
// sinon GTM lirait aussi les gtag('event', …) destinés à GA4 et pourrait
// déclencher ses conversions une seconde fois.
//
// Les pages vues « basées sur l'historique du navigateur » sont désactivées
// dans le flux GA4 : chaque page_view part d'ici, au chargement initial puis
// à chaque navigation Inertia.
//
// Consent Mode v2 : l'état par défaut est posé dans les deux files par le
// <head> rendu côté serveur (resources/views/inertia_layout.edge), avant ce
// module. S'il manque, rien n'est chargé — mieux vaut aucune mesure qu'une
// balise partie sans état de consentement, donc en « tout accordé ».
// ─────────────────────────────────────────────────────────────

/** Propriété GA4 du site pro. */
export const GA_SITE_ID = 'G-NGVN28TSYM'

/** Toutes les propriétés chargées : à chaque instant, une seule reçoit quoi que ce soit. */
const GA_IDS = [GA_SITE_ID]

/**
 * Seuls hôtes mesurés. Partout ailleurs — localhost, staging, aperçus — gtag.js
 * n'est même pas téléchargé. Doit rester identique à la liste du script du
 * <head>, qui décide pour GTM.
 */
const PRODUCTION_HOSTNAMES = ['loicroth.com', 'www.loicroth.com']

/** Routes jamais mesurées, par aucune propriété. */
export const EXCLUDED_ROUTES = ['/admin', '/amdin', '/login']

/** Doit rester identique au nom utilisé par le script du <head>. */
const GA_DATA_LAYER = 'gaDataLayer'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gaDataLayer?: unknown[]
    [gaDisable: `ga-disable-${string}`]: boolean | undefined
  }
}

/**
 * Même garde que inertia/lib/animations.ts : au rendu serveur il n'y a pas de
 * `window`, donc ni balise ni bannière.
 */
export const IS_SERVER = typeof window === 'undefined'

/** `/admin` et `/admin/…`, mais pas `/administration`. */
const matchesRoute = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

/** Propriété GA4 qui reçoit cette route, ou null si elle n'est pas mesurée. */
export const gaIdFor = (pathname: string): string | null => {
  if (EXCLUDED_ROUTES.some((prefix) => matchesRoute(pathname, prefix))) return null
  return GA_SITE_ID
}

/** Faux au rendu serveur et hors production. */
export const isTrackingEnabled = (): boolean =>
  !IS_SERVER && PRODUCTION_HOSTNAMES.includes(window.location.hostname)

function gtag(..._args: unknown[]): void {
  window.gaDataLayer = window.gaDataLayer || []
  // gtag.js exige l'objet `arguments` brut, pas un tableau : il inspecte
  // `arguments.length` et le type de chaque position. Un rest array serait
  // poussé comme un seul argument et silencieusement ignoré.
  window.gaDataLayer.push(arguments)
}

let loaded = false

const pathnameOf = (url: string): string => url.split(/[?#]/)[0]

/**
 * Coupe toutes les propriétés sauf celle de la route. `ga-disable-<ID>` bloque
 * TOUT envoi vers cet ID, y compris les événements automatiques de la mesure
 * améliorée (défilement, clics sortants, formulaires) que le code ne voit pas
 * passer : c'est ce qui garantit que /admin ne remonte rien du tout.
 *
 * Appelée au plus tôt de chaque navigation Inertia (inertia/boot.tsx), puis de
 * nouveau par `trackPageView`.
 */
export const applyRouteRules = (url: string): string | null => {
  if (!loaded) return null
  const activeId = gaIdFor(pathnameOf(url))
  for (const id of GA_IDS) window[`ga-disable-${id}`] = id !== activeId
  return activeId
}

let lastTrackedUrl: string | null = null

/**
 * Vue de page. `url` est le chemin, avec sa query éventuelle : un changement
 * de query compte comme une nouvelle vue, comme avant.
 *
 * L'URL déjà comptée est retenue ici, au niveau du module, et non dans le
 * composant appelant : la mise en page est recréée à chaque visite, un état
 * local repartirait de zéro et laisserait passer un doublon à chaque fois. Le
 * premier appel de la mise en page est absorbé de la même façon : la page
 * d'atterrissage a déjà été comptée par `initAnalytics`.
 */
export const trackPageView = (url: string): void => {
  if (!loaded || url === lastTrackedUrl) return
  lastTrackedUrl = url
  const activeId = applyRouteRules(url)
  if (!activeId) return
  gtag('event', 'page_view', {
    send_to: activeId,
    page_location: window.location.href,
    page_title: document.title,
  })
}

/**
 * À appeler UNE fois, depuis l'entrée client (inertia/app.tsx) : elle
 * s'exécute avant le premier paint, sans attendre React. Repousser le
 * chargement au démarrage du bundle referait perdre la première vue des
 * visiteurs venus d'une annonce, qui repartent souvent avant (voir le <head>).
 */
export const initAnalytics = (): void => {
  if (loaded || !isTrackingEnabled() || !window.__lrConsent) return
  loaded = true

  // Avant le moindre `config` : un ID coupé d'emblée n'envoie rien, pas même
  // les événements que gtag.js émet de lui-même au démarrage.
  applyRouteRules(window.location.pathname)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_SITE_ID}&l=${GA_DATA_LAYER}`
  document.head.appendChild(script)

  gtag('js', new Date())
  for (const id of GA_IDS) gtag('config', id, { send_page_view: false })

  trackPageView(window.location.pathname + window.location.search)
}

type TrackOptions = {
  /**
   * Ajouté au seul message GTM : les coordonnées des conversions avancées
   * Google Ads (`user_data`) ne partent jamais vers GA4.
   */
  gtmOnly?: Record<string, unknown>
  /** `false` : l'événement ne sert qu'aux balises GTM, GA4 ne le reçoit pas. */
  ga?: boolean
}

/**
 * Événement, sous les mêmes règles que les vues de page : rien hors
 * production, rien sur une route exclue.
 *
 * Il part deux fois, vers deux destinataires distincts :
 * - GA4, par gtag('event', …), avec `send_to` sur la propriété de la route ;
 * - GTM, par un objet { event } dans `window.dataLayer` : c'est ce qui
 *   déclenche les balises de conversion Google Ads.
 *
 * Les événements partent quel que soit le consentement : c'est tout l'intérêt
 * du mode avancé. Sans accord, les balises les convertissent en pings
 * anonymes que Google modélise ; avec accord, ils remontent nominativement.
 */
export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {},
  { gtmOnly, ga = true }: TrackOptions = {}
): void => {
  if (!loaded) return
  const activeId = gaIdFor(window.location.pathname)
  if (!activeId) return
  if (ga) gtag('event', name, { ...params, send_to: activeId })
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: name, ...params, ...gtmOnly })
}
